import { defineStore } from 'pinia'

interface TablePreferences {
  visibleColumns?: string[]
}

interface TablePreferencesState {
  tables: Record<string, TablePreferences>
}

const STORAGE_KEY = 'tablePreferences'

function isValidTablePreferencesState(data: unknown): data is TablePreferencesState {
  if (!data || typeof data !== 'object') {
    return false
  }

  const obj = data as Record<string, unknown>

  if (!obj.tables || typeof obj.tables !== 'object') {
    return false
  }

  for (const table of Object.values(obj.tables)) {
    if (!table || typeof table !== 'object') {
      return false
    }

    const t = table as Record<string, unknown>

    if (
      t.visibleColumns !== undefined &&
      (!Array.isArray(t.visibleColumns) || !t.visibleColumns.every(col => typeof col === 'string'))
    ) {
      return false
    }
  }

  return true
}

export const useTablePreferencesStore = defineStore('tablePreferences', {
  state: (): TablePreferencesState => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (isValidTablePreferencesState(parsed)) {
          return parsed
        } else {
          console.warn('Invalid table preferences structure in localStorage, falling back to default state.')
        }
      }
    } catch (_error) {
      console.warn('Failed to load table preferences from localStorage:', _error)
    }

    return {
      tables: {},
    }
  },

  getters: {
    getTablePrefs: state => {
      return (tableKey: string): TablePreferences => {
        return state.tables[tableKey] || {}
      }
    },

    getVisibleColumns: state => {
      return (tableKey: string): string[] | undefined => {
        return state.tables[tableKey]?.visibleColumns
      }
    },
  },

  actions: {
    setVisibleColumns(tableKey: string, columns: string[]) {
      if (!this.tables[tableKey]) {
        this.tables[tableKey] = {}
      }
      this.tables[tableKey].visibleColumns = columns
      this.saveToLocalStorage()
    },

    updateTablePrefs(tableKey: string, prefs: Partial<TablePreferences>) {
      this.tables[tableKey] = {
        ...this.tables[tableKey],
        ...prefs,
      }
      this.saveToLocalStorage()
    },

    clearTablePrefs(tableKey: string) {
      delete this.tables[tableKey]
      this.saveToLocalStorage()
    },

    clearAllPrefs() {
      this.tables = {}
      this.saveToLocalStorage()
    },

    saveToLocalStorage() {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.$state))
      } catch (_error) {
        console.warn('Failed to save table preferences to localStorage:', _error)
      }
    },
  },
})
